import React, {useState, useRef, useCallback} from 'react';
import sessionStore from 'client/Session/SessionStore';
import {
  FindAndApply,
  Button,
  Icon,
  Popover,
  ListGroup,
  ListGroupItem,
  Checkbox,
  Label,
  Text,
  ButtonGroup,
  addGenericErrorToast
} from '@albert-io/atomic';
import type {ButtonVariant, ButtonColor} from '@albert-io/atomic/atoms/Button/Button.react';
import type {List} from 'immutable';
import type {StandardSetModelV1} from 'resources/augmented/StandardSet/StandardSetModel.v1';
import {resource} from '@albert-io/json-api-framework/request/builder';
import classnames from 'classnames';
import {pluralize} from 'lib/stringUtils';

import '../advanced-search.scss';

interface Props {
  onApply: (standardSets: List<StandardSetModelV1>) => void;
  subjectId?: string;
  standardSets: List<StandardSetModelV1>;
}

type FilterOption = {
  [index: string]:
    | {
        search_keyword: string;
      }
    | boolean;
};

const StandardSetFilter = ({onApply, subjectId, standardSets}: Props) => {
  const [showPopover, setShowPopover] = useState(false);

  const buttonRef = useRef(null);

  const getQuery = useCallback(
    (searchString) => {
      const anyOfOptions: FilterOption[] = [
        {
          title: {
            search_keyword: searchString
          }
        },
        {
          description: {
            search_keyword: searchString
          }
        }
      ];
      // only show personalized results if user hasn't typed in any input
      if (searchString === '') {
        anyOfOptions.push({
          'meta.included_in_personalization_settings': true
        });
      }
      return resource('standard_sets_v1')
        .mandarkEndpoint(['standard_sets_v1'])
        .withMeta('standard_set_v1')
        .customQuery({
          meta: {
            context: {
              user: {
                id: sessionStore.getUserId(),
                personalization_settings: true
              }
            }
          }
        })
        .filter(
          subjectId
            ? {
                all_of: [
                  {
                    any_of: anyOfOptions
                  },
                  {
                    standards_v1: {
                      questions_v3: {
                        question_set_v1: {
                          subject_v2: subjectId
                        }
                      }
                    }
                  }
                ]
              }
            : {
                any_of: anyOfOptions
              }
        )
        .pageSize(500)
        .customQuery({meta: {context: {search: {fields: 'title,description'}}}});
    },
    [subjectId]
  );

  return (
    <>
      <span
        className={classnames('flex rounded-x1 background-transparent', {
          'advanced-search__standard-sets-button-wrapper-active': standardSets.size > 0
        })}
        ref={buttonRef}
      >
        <Button
          variant={(standardSets.size ? undefined : 'outlined') as ButtonVariant}
          color={(standardSets.size ? 'inherit' : 'secondary') as ButtonColor}
          onClick={() => setShowPopover(!showPopover)}
        >
          {`${standardSets.size > 0 ? standardSets.size : ''} ${pluralize(
            'Standard Set',
            standardSets.size
          )}`}
          <Icon icon='caret-down' className='ml-x2' />
        </Button>
      </span>
      {showPopover && (
        <Popover
          usePortal={false}
          border='none'
          expanded={showPopover}
          position='bottom-start'
          // null is a supported runtime value (Card.paddingLevel is `PaddingLevel | null`); Popover's prop type is too narrow
          // @ts-expect-error Popover paddingLevel should be `number | null` to match Card
          paddingLevel={null}
          targetRef={buttonRef}
          modifiers={{flip: {enabled: false}}}
        >
          <FindAndApply
            className='advanced-search_filter-dropdown w-full'
            query={getQuery}
            onApply={onApply}
            onDismiss={() => setShowPopover(!showPopover)}
            fetchInitial
            defaultSelectedItems={standardSets}
            onError={addGenericErrorToast}
          >
            <FindAndApply.Header paddingLevel={1}>
              {({HeaderSearchInput}) => (
                <HeaderSearchInput
                  variant
                  size='s'
                  placeholder='Search standard sets'
                  className='standard-set-filter__search-input'
                />
              )}
            </FindAndApply.Header>
            <FindAndApply.Body paddingLevel={0}>
              {({searchResults, selectedItems, handleChange, searchString}) => {
                if (
                  !searchString &&
                  !searchResults.some((result) =>
                    (result as StandardSetModelV1).getMeta().isIncludedInPersonalizationSettings()
                  )
                ) {
                  return (
                    <FindAndApply.Message>
                      Search by Standard set name such as &quot;Common Core Math&quot; or &quot;ACT
                      WorkKey Skills&quot;
                    </FindAndApply.Message>
                  );
                }

                // don't show "pinned suggestions" if user has input anything into the search bar
                if (searchString.length) {
                  return (
                    <ListGroup as='ul' border='none' className='mt-x0 pt-x0'>
                      {searchResults.map((result) => {
                        // Immutable 3.8 typings give `result` as possibly-undefined; it is always a populated model here
                        const item = result!;
                        const standardSet = item as StandardSetModelV1;
                        return (
                          <ListGroupItem
                            key={standardSet.getId()}
                            paddingLevel={1}
                            border={false}
                            as='li'
                            className='flex items-center gap-x1'
                            onClick={() => handleChange(item)}
                          >
                            <Checkbox
                              aria-hidden={false}
                              checked={selectedItems.has(item)}
                              size='s'
                              className='ml-x1'
                              onClick={(e) => {
                                handleChange(item);
                                e.stopPropagation();
                              }}
                            />
                            <Label htmlFor={standardSet.getId()} className='cursor-pointer'>
                              <Text size='m'>{standardSet.getTitle()}</Text>
                            </Label>
                          </ListGroupItem>
                        );
                      })}
                    </ListGroup>
                  );
                }
                return (
                  <ListGroup as='ul' border='none' className='mt-x0 pt-x0'>
                    {selectedItems.map((selectedItem) => {
                      // Immutable 3.8 typings give `selectedItem` as possibly-undefined; it is always a populated model here
                      const item = selectedItem!;
                      const standardSet = item as StandardSetModelV1;
                      return (
                        <ListGroupItem
                          key={standardSet.getId()}
                          paddingLevel={1}
                          border={false}
                          as='li'
                          className='flex items-center gap-x1'
                          onClick={() => handleChange(item)}
                        >
                          <Checkbox
                            aria-hidden={false}
                            checked
                            size='s'
                            className='ml-x1'
                            onClick={(e) => {
                              handleChange(item);
                              e.stopPropagation();
                            }}
                          />
                          <Label htmlFor={standardSet.getId()} className='cursor-pointer'>
                            <Text size='m'>{standardSet.getTitle()}</Text>
                          </Label>
                        </ListGroupItem>
                      );
                    })}
                    <ListGroupItem paddingLevel={0} />
                    <li className='mb-x1 flex items-start gap-x1 border-b border-subtle px-x2 py-x1 content-tertiary'>
                      Suggestions
                    </li>
                    {searchResults
                      .filter((result) => !selectedItems.has(result!))
                      // this should be handled by the query but mandark can't sort booleans
                      .sort((item) =>
                        (item as StandardSetModelV1)
                          .getMeta()
                          .isIncludedInPersonalizationSettings()
                          ? -1
                          : 1
                      )
                      .map((result) => {
                        // Immutable 3.8 typings give `result` as possibly-undefined; it is always a populated model here
                        const item = result!;
                        const standardSet = item as StandardSetModelV1;
                        return (
                          <ListGroupItem
                            key={standardSet.getId()}
                            paddingLevel={1}
                            border={false}
                            as='li'
                            className='flex items-center gap-x1'
                            onClick={() => handleChange(item)}
                          >
                            <Checkbox
                              aria-hidden={false}
                              className='ml-x1'
                              size='s'
                              onClick={(e) => {
                                handleChange(item);
                                e.stopPropagation();
                              }}
                            />
                            <Label htmlFor={standardSet.getId()} className='cursor-pointer'>
                              <Text size='m'>{standardSet.getTitle()}</Text>
                            </Label>
                          </ListGroupItem>
                        );
                      })}
                  </ListGroup>
                );
              }}
            </FindAndApply.Body>
            <FindAndApply.Footer paddingLevel={1}>
              {({ApplyButton, onDismiss}) => (
                <ButtonGroup align='right'>
                  <Button variant='text' color='secondary' size='s' onClick={onDismiss}>
                    Cancel
                  </Button>
                  <ApplyButton variant='text' size='s'>
                    Done
                  </ApplyButton>
                </ButtonGroup>
              )}
            </FindAndApply.Footer>
          </FindAndApply>
        </Popover>
      )}
    </>
  );
};

export default StandardSetFilter;
